import React from 'react';
import { Facebook, Linkedin, Twitter } from 'lucide-react';

/**
 * SocialPreview
 *
 * Renders three small mock cards that show how a given page will appear when
 * shared on Facebook, LinkedIn, and Twitter/X. Mirrors the backend OG-tag
 * fallback chain (page.og_* → page meta → tenant logo/favicon) so the editor
 * sees the same image the bots will see.
 *
 * Props:
 *   title:        page title (string)
 *   description:  meta_description or og_description (string)
 *   image:        og_image URL — already resolved to whatever the renderer will use
 *   url:          canonical page URL (string), e.g. https://customer.com/about
 *   siteName:     tenant name (used in the "by domain" footer of the cards)
 */
const SocialPreview = ({ title, description, image, url, siteName }) => {
  const safeTitle = (title || siteName || 'Untitled page').trim();
  const safeDesc = (description || '').trim();
  let display = (siteName || '').toLowerCase();
  try {
    if (url) display = new URL(url).hostname.replace(/^www\./, '');
  } catch (e) { /* keep fallback */ }

  const cardWrap = (Icon, label, body) => (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
      data-testid={`social-preview-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      key={label}
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
        <Icon size={14} className="text-slate-500" />
        <span className="text-xs font-medium text-slate-600">{label}</span>
      </div>
      {body}
    </div>
  );

  const facebookBody = (
    <>
      {image ? (
        <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-[1.91/1] bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-400 text-xs">
          No image
        </div>
      )}
      <div className="px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 truncate">{display}</div>
        <div className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 mt-0.5">{safeTitle}</div>
        {safeDesc && <div className="text-xs text-slate-500 line-clamp-2 mt-1">{safeDesc}</div>}
      </div>
    </>
  );

  const linkedInBody = (
    <>
      {image ? (
        <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-[1.91/1] bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-blue-400 text-xs">
          No image
        </div>
      )}
      <div className="px-3 py-2">
        <div className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">{safeTitle}</div>
        <div className="text-[10px] text-slate-500 mt-1 truncate">{display}</div>
      </div>
    </>
  );

  const twitterBody = (
    <>
      {image ? (
        <div className="relative aspect-[1.91/1] bg-slate-100 overflow-hidden">
          <img src={image} alt="" className="w-full h-full object-cover" />
          <div className="absolute left-2 bottom-2 right-2 bg-black/55 text-white text-xs px-2 py-1 rounded">
            <div className="font-semibold leading-tight line-clamp-1">{safeTitle}</div>
          </div>
        </div>
      ) : (
        <div className="aspect-[1.91/1] bg-gradient-to-br from-slate-900 to-slate-700 flex flex-col items-start justify-end p-3">
          <div className="text-sm font-semibold text-white line-clamp-2 leading-tight">{safeTitle}</div>
          <div className="text-[10px] text-slate-300 mt-1 truncate">{display}</div>
        </div>
      )}
      <div className="px-3 py-1.5 text-[10px] text-slate-500 truncate">{display}</div>
    </>
  );

  return (
    <div className="space-y-3" data-testid="social-preview-panel">
      {cardWrap(Facebook, 'Facebook', facebookBody)}
      {cardWrap(Linkedin, 'LinkedIn', linkedInBody)}
      {cardWrap(Twitter, 'Twitter / X', twitterBody)}
      <p className="text-[10px] text-slate-400 leading-tight">
        Preview only — actual rendering may differ slightly between platforms. Re-share via
        the official debuggers to refresh their image caches after major changes.
      </p>
    </div>
  );
};

export default SocialPreview;
